/**
 * What sits beside an item (`ChildItem.aids`, from its Topic's subjects): the
 * periodic table on a chemistry item, in practice and in tests alike, since a
 * school test allows it. Many prompts say „Naudokis periodine lentele", and a
 * child without a paper table should fail the Move, not a lookup.
 *
 * Keyboard-first, and the answer keeps the keyboard: `Alt+P` (read by code,
 * `KeyP`, so any layout; Option+P on a Mac; never AltGr, which types) opens
 * and closes it, `Esc` closes an open one. Every bare key belongs to some
 * answer surface (surfaces.md §5.2), so the chord is the one way in that
 * typing cannot hit. A click on the table never takes the focus off her slot.
 *
 * How often she needs it is evidence: `aid.opened {aid, item, via}` and
 * `aid.closed {opened, by, open_ms}`, and an `aid` entry in the variant's
 * trace, so a pause spent reading the table is told apart from one spent
 * thinking. The item leaving her screen closes it (`item_end`), so an
 * opening belongs to exactly one item.
 */
import type { KeyLike } from "../surfaces/keys";
import type { Entry } from "../surfaces/slots";

export type Aid = "periodic_table";
export const PERIODIC: Aid = "periodic_table";

export type AidVia = "key" | "click";
export type AidCloseBy = AidVia | "item_end" | "session_end";

/** What a key does to the aid: Alt+P toggles it, Esc closes it while open. */
export function aidKey(k: KeyLike, open: boolean): "toggle" | "close" | null {
  if (k.repeat) return null;
  if (k.code === "KeyP" && k.altKey && !k.ctrlKey && !k.metaKey && !k.shiftKey) return "toggle";
  if (open && k.key === "Escape" && !k.altKey && !k.ctrlKey && !k.metaKey) return "close";
  return null;
}

/** An open aid: its `aid.opened` id and `performance.now()` when it opened. */
export interface AidOpen {
  id: string;
  at: number;
}

export function openedBody(aid: Aid, item: string, via: AidVia): Record<string, unknown> {
  return { aid, item, via };
}

export function closedBody(o: AidOpen, by: AidCloseBy, now: number): Record<string, unknown> {
  return { opened: o.id, by, open_ms: Math.max(0, Math.round(now - o.at)) };
}

/** The trace entry (surfaces.md §3.5): the key as pressed, or the click on its button. */
export function aidEntry(aid: Aid, what: "open" | "close", k: KeyLike | null): Entry {
  return k ? [k.code, k.key, "", "aid", `${aid}:${what}`] : ["Mouse0", `btn:${aid}`, "", "aid", `${aid}:${what}`];
}
